/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { debounce as _debounce, isEqual as _isEqual } from 'lodash';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { Box, Button } from '@mui/material';
import { IChangeEvent } from '@rjsf/core';
import Form from '@rjsf/mui';
import { RegistryWidgetsType } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';

import { Namespaces } from '../../../../i18n';
import { ClusterParameterResponse } from '../../../../types/cluster/ClusterParameterResponse';
import { ClusterUpdateRequestBody } from '../../../../types/cluster/ClusterUpdateRequestBody';
import { UpdateClusterDetailsParameterIdentification } from '../../../../types/cluster/UpdateClusterDetailsParameterIdentification';
import { fetchParametersOfCluster } from '../../../../utils/queryFns';
import { removeFormDataEquals } from '../../../../utils/removeFormDataEquals';
import { SkeletonTabContent } from '../SkeletonTabContent/SkeletonTabContent';
import { CustomCheckboxWidget } from './components/CustomCheckboxWidget/CustomCheckboxWidget';
import { CustomCollapsibleField } from './components/CustomCollapsibleField/CustomCollapsibleField';
import { CustomFieldTemplate } from './components/CustomFieldTemplate/CustomFieldTemplate';
import { CustomSelectWidget } from './components/CustomSelectWidget/CustomSelectWidget';
import { CustomTextWidget } from './components/CustomTextWidget/CustomTextWidget';
import { applyI18nToRJSFErrors } from './components/ParametersTab.utils';
import { WidgetMenu } from './components/WidgetMenu/WidgetMenu';
import { ParametersTabStyles } from './ParametersTab.styles';
import { FormContextType, HandleMenuOpenArgs, MenuDataType } from './ParametersTab.types';

type Props = {
  updateData: (data: ClusterUpdateRequestBody) => void;
  identificationData: UpdateClusterDetailsParameterIdentification;
};

const NullTitleField = memo(() => null);

const fields = {
  collapsible: CustomCollapsibleField,
  TitleField: NullTitleField,
};

export const ParametersTab = memo<Props>(props => {
  const { updateData, identificationData } = props;
  const { t } = useTranslation(Namespaces.Cluster);
  const classes = ParametersTabStyles();

  const customWidgets: RegistryWidgetsType = {
    TextWidget: CustomTextWidget as any,
    CheckboxWidget: CustomCheckboxWidget as any,
    SelectWidget: CustomSelectWidget as any,
  };

  const parametersQuery = useQuery(
    [fetchParametersOfCluster.key, { ...identificationData }],
    fetchParametersOfCluster.fn,
    { refetchOnWindowFocus: false },
  );

  const [initialParameters, setInitialParameters] = useState<ClusterParameterResponse>();
  const [parameters, setParameters] = useState<ClusterParameterResponse>();
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [hasErrors, setHasErrors] = useState<boolean>(false);

  const debouncedSetIsDirty = useMemo(() => _debounce((value: boolean) => setIsDirty(value), 75), [setIsDirty]);
  const debouncedSetHasErrors = useMemo(() => _debounce((value: boolean) => setHasErrors(value), 75), [setHasErrors]);

  useEffect(() => {
    setInitialParameters(parametersQuery.data?.payload);
    setParameters(parametersQuery.data?.payload);
  }, [parametersQuery.data]);

  const onSubmit = (e: IChangeEvent) => {
    const filteredFormData = removeFormDataEquals(initialParameters?.formData, e.formData);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    try {
      updateData({ configuration: { postgresqlConfig: filteredFormData } });
    } catch (e) {
      /* empty */
    }
    setInitialParameters({ ...initialParameters!, formData: e.formData });
    setIsDirty(false);
    setHasErrors(e.errors.length > 0);
  };

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const [menuData, setMenuData] = useState<MenuDataType>({
    propName: null,
    propValue: null,
  });

  const handleMenuOpen = useCallback((args: HandleMenuOpenArgs) => {
    const { event, name, value } = args;
    setMenuAnchor(event.currentTarget);
    setMenuData({
      propName: name,
      propValue: value ? value.toString() : null,
    });
  }, []);

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const onCopy = () => {
    handleMenuClose();
  };

  const formContext = useMemo<FormContextType>(() => {
    return {
      handleMenuOpen,
    };
  }, [handleMenuOpen]);

  const transformErrors = useMemo(() => applyI18nToRJSFErrors(t), [t]);

  const handleFormChange = (e: IChangeEvent) => {
    if (parameters && initialParameters) {
      setParameters({ ...parameters, formData: e.formData });
      debouncedSetHasErrors(e.errors.length > 0);
      debouncedSetIsDirty(!_isEqual(e.formData, initialParameters.formData));
    }
  };

  if (parametersQuery.status === 'loading') {
    return <SkeletonTabContent />;
  }

  if (initialParameters && parameters) {
    if (parameters.formData.error !== undefined) {
      return parameters.formData.error;
    }

    return (
      <div className={classes.root}>
        <Form
          className={classes.form}
          transformErrors={transformErrors}
          schema={parameters.schema}
          uiSchema={parameters.uiSchema}
          formData={parameters.formData}
          onSubmit={onSubmit}
          showErrorList={false}
          liveValidate
          validator={validator}
          templates={{
            TitleFieldTemplate: NullTitleField,
            FieldTemplate: CustomFieldTemplate,
          }}
          onChange={handleFormChange}
          widgets={customWidgets}
          fields={fields}
          formContext={formContext}
        >
          <Box className={classes.buttonWrapper}>
            <Button
              color="primary"
              variant="contained"
              type="submit"
              disabled={!isDirty || hasErrors}
              data-cy="cluster-page-parameters-tab-submit-button"
            >
              {t('submit')}
            </Button>
          </Box>
        </Form>

        <WidgetMenu
          menuAnchor={menuAnchor}
          menuClose={handleMenuClose}
          propName={menuData.propName}
          propValue={menuData.propValue}
          onCopy={onCopy}
        />
      </div>
    );
  }
});
